(ns ui.view.entity-creator.sub
  (:require
   [common.localization :as loc]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.entity-type-names :as etn]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [reagent.ratom :as ratom]
   [ui.state.session.sub :as session-sub]
   [ui.util :as uu]
   [ui.view.entity-creator.model :as m]
   [ui.view.entity-creator.utils :as u]))

(rf/reg-sub
 ;; Shouldn't be used directly - ident needed
 m/root-state
 (fn [db _]
   (get db m/root)))

(rf/reg-sub
 ;; State specific to a single instance of creator
 m/state
 (fn [[_ _args]]
   {:root-state (rf/subscribe [m/root-state])})
 (fn [{:keys [root-state]}
      [_ {:keys [ident]}]]
   (get-in root-state ident)))

(rf/reg-sub
 m/pipeline
 (fn [[_ args]]
   {:state (rf/subscribe [m/state args])})
 (fn [{:keys [state]} _]
   (get state m/pipeline-key)))

(rf/reg-sub
 m/stage
 (fn [[_ args]]
   {:state (rf/subscribe [m/state args])})
 (fn [{:keys [state]} _]
   (get state m/stage-key)))

(rf/reg-sub
 m/processing?
 (fn [[_ args]]
   {:state (rf/subscribe [m/state args])})
 (fn [{:keys [state]} _]
   (get state m/processing-key)))

(rf/reg-sub
 m/entity-type
 (fn [[_ args]]
   {:state (rf/subscribe [m/state args])})
 (fn [{:keys [state]} _]
   (get state m/entity-type-key)))

(rf/reg-sub
 m/created-entity
 (fn [[_ args]]
   {:state (rf/subscribe [m/state args])})
 (fn [{:keys [state]} _]
   (get state m/created-entity-key)))

(defn stages-reaction
  [db-atom [_ {:keys [ident pipeline-id] :as _params}]]
  (ratom/reaction
   (let [pipeline-id (or pipeline-id
                         (->> (m/pipeline-full-path ident)
                              (get-in @db-atom)
                              :db/id))
         stages (->> (session/pipeline-stages pipeline-id)
                     (sort-by :common/ord)
                     doall)]
     stages)))

(rf/reg-sub-raw
 m/stages
 #'stages-reaction)

(defn subview
  [{:keys [entity-type created-entity processing?]}
   [_ _args]]
  (cond (and created-entity (not processing?))
        :finished

        entity-type
        :creator-one

        :else
        :loading))

(rf/reg-sub
 m/subview
 (fn [[_ args]]
   {:created-entity (rf/subscribe [m/created-entity args])
    :processing? (rf/subscribe [m/processing? args])
    :entity-type (rf/subscribe [m/entity-type args])})
 #'subview)

(defn get-field-schema
  [entity-type attribute]
  (-> (registry/get-schema @session/tenant-schema entity-type)
      (get attribute)))

(defn resolve-title
  [{:keys [entity-type subview created-entity]}
   [_ {:keys [for-attribute for-entity-type]}]]
  (let [entity-type-name (etn/get-name entity-type :en)]
    (cond (and (= subview :creator-one)
               for-attribute)
          (loc/tr :create-entity/attribute-title
                  [entity-type-name
                   (loc/field-name for-attribute
                                   (get-field-schema for-entity-type for-attribute))])

          (= subview :creator-one)
          (loc/tr :create-entity/title [entity-type-name])

          created-entity
          (loc/tr :success)

          :else "TITLE")))

(rf/reg-sub
 m/title
 (fn [[_ args]]
   {:entity-type (rf/subscribe [m/entity-type args])
    :created-entity (rf/subscribe [m/created-entity args])
    :subview (rf/subscribe [m/subview args])})
 #'resolve-title)

(defn raw-config-reaction
  [_db-atom [_ {:keys [pipeline] :as args}]]
  (ratom/reaction
   (let [pipeline (or pipeline
                      @(rf/subscribe [m/pipeline args]))
         config (if pipeline
                  (:pipeline/creator-config pipeline)
                  (:tenant/entity-creation-config
                   @(rf/subscribe [::session-sub/tenant])))]
     config)))

(rf/reg-sub-raw
 m/raw-config
 #'raw-config-reaction)

(defn system-mandatory-reaction
  [_db-atom [_ {:keys [entity-type pipeline]}]]
  (ratom/reaction
   (let [tenant @(rf/subscribe [::session-sub/tenant])]
     (->> (ecu/mandatory-fields
           {:tenant tenant
            :pipeline (or pipeline
                          @(rf/subscribe [m/pipeline]))
            :tenant-schema @session/tenant-schema
            :field-configs (:tenant/field-configurations
                            tenant)
            :tenant-modules @session/tenant-modules} entity-type)
          keys
          set))))

(rf/reg-sub-raw
 m/system-mandatory
 #'system-mandatory-reaction)

(defn config
  ;; If `:fields-config` is provided in args it short-circuits the resolve.
  ;; Must match what was passed to `ui.view.entity-creator.editor/view`'s
  ;; second-map so render and validation stay aligned.
  [{:keys [raw-config pipeline stages system-mandatory]}
   [_ {:keys [config-path entity-type target-type fields-config] :as _args}]]
  (or fields-config
      (u/resolve-entity-config
       {:entity-type entity-type
        :target-type target-type
        :config-path config-path
        :raw-config raw-config
        :stages stages
        :pipeline pipeline
        :system-mandatory system-mandatory})))

(rf/reg-sub
 m/config
 (fn [[_ args]]
   {:raw-config (rf/subscribe [m/raw-config args])
    :pipeline (rf/subscribe [m/pipeline args])
    :stages (rf/subscribe [m/stages args])
    :system-mandatory (rf/subscribe [m/system-mandatory args])})
 #'config)

;;not needed?
(defn config-fields
  [{:keys [config stage]}
   [_ {:keys []}]]
  (let [stage-id (:db/id stage)]
    (get-in config [:field :stages stage-id])))

(rf/reg-sub
 m/config-fields
 (fn [[_ args]]
   {:config (rf/subscribe [m/config args])
    :stage (rf/subscribe [m/stage args])})
 #'config-fields)

(defn field-schemas-reaction
  [_db-atom [_ {:keys [entity-type] :as args}]]
  (ratom/reaction
   (let [pipeline @(rf/subscribe [m/pipeline args])
         tenant @session/tenant
         field-configs (:tenant/field-configurations tenant)
         ctx {:pipeline pipeline
              :tenant @session/tenant
              :tenant-schema @session/tenant-schema
              :field-configs field-configs}
         field-schemas (ecu/configurable-creator-fields ctx entity-type)]
     field-schemas)))

(rf/reg-sub-raw
 m/field-schemas
 #'field-schemas-reaction)

(rf/reg-sub
 m/edits
 (fn [[_ args]]
   {:state (rf/subscribe [m/state args])})
 (fn [{:keys [state]} _]
   (get state m/edits-key)))

(rf/reg-sub
 m/entity-edits
 (fn [[_ args]]
   {:edits (rf/subscribe [m/edits args])})
 (fn [{:keys [edits]} _]
   (get edits m/entity-edits-key)))

(rf/reg-sub
 m/custom-edits
 (fn [[_ args]]
   {:edits (rf/subscribe [m/edits args])})
 (fn [{:keys [edits]} [_ {:keys [entity-edits-path]}]]
   (get-in edits entity-edits-path)))

(defn float-if-bigdec
  [value]
  (cond-> value
    (uu/is-bigdec? value) uu/bigdec->float))

(rf/reg-sub
 m/edited-value
 (fn [[_ args]]
   {:custom-edits (rf/subscribe [m/custom-edits
                                 (select-keys
                                  args
                                  [:ident
                                   :entity-edits-path])])})
 (fn [{:keys [custom-edits]} [_ {:keys [attribute entity] :as _args}]]
   (let [kw? (keyword? attribute)
         in-edits? (if kw?
                     (contains? custom-edits attribute)
                     (cu/contains-path? custom-edits attribute))
         ;; if editing an existing entity and the attribute is not yet edited,
         ;; read from original entity:
         read-entity? (and entity
                           (not
                            in-edits?))
         get-value
         (fn [entity-or-edits]
           (if (keyword? attribute)
             (get entity-or-edits attribute)
             (get-in entity-or-edits attribute)))
         value (if read-entity?
                 (get-value entity)
                 (get-value custom-edits))]
     (float-if-bigdec value))))

(defn field-errors
  [{:keys [_edited-value entity-validations]} [_ {:keys [attribute _field-schema]}]]
  (get entity-validations attribute))

(rf/reg-sub
 m/field-errors
 (fn [[_ args]]
   {:edited-value (rf/subscribe [m/edited-value args])
    :entity-validations (rf/subscribe [m/entity-validations
                                       (dissoc args
                                               :attribute
                                               :field-schema
                                               :field-config)])})
 field-errors)

(defn validate-fields
  [{:keys [custom-edits config field-schemas stage existing-entity pipeline] :as _args}
   [_ {:keys [entity-type field-schema-overrides open-entity-fn]}]]
  (let [stage-id (:db/id stage)
        available-fields-set (->> field-schemas
                                  keys
                                  set)
        configured-fields (->> config
                               :fields
                               (mapv (fn [{:keys [attribute] :as field-config}]
                                       (let [field-config-path
                                             (if stage-id
                                               [:stages stage-id]
                                               [:create])
                                             setting (get-in field-config field-config-path)]
                                         [attribute setting])))
                               (into {}))]
    (->> config
         :fields
         (filterv (fn [{:keys [attribute]}]
                    (contains? #{:mandatory :system-mandatory :optional}
                               (get-in configured-fields
                                       [attribute :setting]))))
         (filterv (fn [{:keys [attribute]}]
                    (contains? available-fields-set attribute)))
         (filterv (fn [{:keys [attribute]}]
                    (f-permissions/field-read-access? attribute entity-type pipeline)))
         (mapv (fn [{:keys [attribute] :as cfg}]
                 (if-not (f-permissions/field-write-access? attribute
                                                            entity-type
                                                            pipeline)
                   (assoc cfg :read-only? true)
                   cfg)))
         (reduce (fn [m {:keys [attribute stages create]}]
                   (let [field-config (if stage-id
                                        (get stages stage-id)
                                        create)
                         field-schema (get field-schemas attribute)
                         overrides (get field-schema-overrides attribute)
                         field-schema (if overrides
                                        (potpuri/deep-merge field-schema overrides)
                                        field-schema)
                         edited? (contains? custom-edits attribute)
                         value (-> (if edited?
                                     (get custom-edits attribute)
                                     (get existing-entity attribute))
                                   float-if-bigdec)
                         errors (when-not (or (:read-only? field-schema)
                                              (:hidden? field-schema))
                                  (u/field-errors
                                   {:attribute attribute
                                    :value value
                                    :entity existing-entity
                                    :field-schema field-schema
                                    :field-config field-config
                                    :open-entity-fn open-entity-fn}))]
                     (if (seq errors)
                       (assoc m attribute (vec errors))
                       m)))
                 {}))))

(defn combine-edits
  "Returns a map representing an entity where an attribute value is an edited value
   if exists in edits, otherwise the original from the entity. `nil` value in edits
   means the value has been cleared"
  [entity edits]
  (->> edits
       (reduce-kv assoc entity)
       (reduce-kv (fn [entity attribute value]
                    (assoc entity attribute
                           (float-if-bigdec value)))
                  {})))

(def custom-error-loc-keys
  "Maps an `:error-key` declared in `ecu/custom-validations` to the localization
   key whose value is shown to the user. Keep in sync with new rules added
   over there."
  {:custom-error/insufficient-person-info
   :custom-error/insufficient-person-info-msg})

(defn custom-validations
  [{:keys [config custom-edits stage] :as _args}
   [_ {:keys [entity-type entity]}]]
  (let [merged-entity (combine-edits entity custom-edits)
        stage-id (:db/id stage)]
    (->> (ecu/failed-custom-validations entity-type
                                        merged-entity
                                        {:fields-config config
                                         :stage-id stage-id})
         (reduce (fn [acc {:keys [error-key]}]
                   (assoc acc error-key
                          [{:error-message
                            (loc/tr (or (custom-error-loc-keys error-key)
                                        :invalid-value))}]))
                 {}))))

(defn entity-validations
  [{:keys [processing?] :as args}
   [_ {:keys [entity-type entity]} :as event]]
  (when-not processing?
    (let [field-validations (validate-fields (assoc args
                                                    :entity-type entity-type
                                                    :existing-entity entity)
                                             event)
          custom-validations (custom-validations args event)]
      (not-empty
       (cond-> field-validations
         (seq custom-validations)
         (merge custom-validations))))))

(rf/reg-sub
 m/entity-validations
 (fn [[_ args]]
   {:custom-edits (rf/subscribe [m/custom-edits args])
    :field-schemas (rf/subscribe [m/field-schemas args])
    :processing? (rf/subscribe [m/processing? args])
    :config (rf/subscribe [m/config args])
    :stage (rf/subscribe [m/stage args])})
 entity-validations)
